import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, X, Pause, Play } from "lucide-react";

interface BunadCarouselProps {
  images: string[];
  title: string;
}

const BunadCarousel = ({ images, title }: BunadCarouselProps) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Duplicate images for seamless infinite scroll
  const duplicatedImages = [...images, ...images, ...images];

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer || isPaused || images.length === 0) return;

    let animationId: number;
    let scrollPosition = scrollContainer.scrollLeft;
    const scrollSpeed = 0.5;

    const animate = () => {
      scrollPosition += scrollSpeed;
      
      // Reset scroll position when we've scrolled past one set of images
      const singleSetWidth = scrollContainer.scrollWidth / 3;
      if (scrollPosition >= singleSetWidth * 2) {
        scrollPosition = singleSetWidth;
      }
      
      scrollContainer.scrollLeft = scrollPosition;
      animationId = requestAnimationFrame(animate);
    };

    // Start from the middle set
    scrollContainer.scrollLeft = scrollContainer.scrollWidth / 3;
    scrollPosition = scrollContainer.scrollLeft;
    
    animationId = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationId);
  }, [isPaused, images.length]);

  if (images.length === 0) {
    return (
      <section className="py-16 bg-secondary">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-primary font-sans text-xs tracking-[0.3em] uppercase mb-4">
            Bildegalleri
          </p>
          <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-6">
            Galleri for {title}
          </h2>
          <p className="text-muted-foreground font-sans">
            Bildegalleri kommer snart.
          </p>
        </div>
      </section>
    );
  }

  const handlePrevious = () => {
    if (selectedIndex !== null) {
      setSelectedIndex(selectedIndex === 0 ? images.length - 1 : selectedIndex - 1);
    }
  };

  const handleNext = () => {
    if (selectedIndex !== null) {
      setSelectedIndex(selectedIndex === images.length - 1 ? 0 : selectedIndex + 1);
    }
  };

  const handleImageClick = (index: number) => {
    // Calculate the actual image index from the duplicated array
    const actualIndex = index % images.length;
    setSelectedIndex(actualIndex);
  };

  return (
    <section className="relative bg-secondary overflow-hidden">
      {/* Header */}
      <div className="text-center pt-16 pb-8 px-6">
        <p className="text-primary font-sans text-xs tracking-[0.3em] uppercase mb-4">
          Bildegalleri
        </p>
        <h2 className="font-serif text-3xl md:text-4xl text-foreground">
          Se {title} i detalj
        </h2>
      </div>

      {/* Infinite Carousel */}
      <div 
        className="relative w-full"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-hidden py-8"
          style={{ scrollBehavior: 'auto' }}
        >
          {duplicatedImages.map((image, index) => (
            <button
              key={index}
              onClick={() => handleImageClick(index)}
              className="flex-shrink-0 h-[60vh] md:h-[70vh] aspect-[3/4] overflow-hidden group cursor-pointer"
            >
              <img
                src={image}
                alt={`${title} bilde ${(index % images.length) + 1}`}
                className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
              />
            </button>
          ))}
        </div>

        {/* Pause/Play indicator */}
        <div className="absolute bottom-4 right-4 bg-background/80 backdrop-blur-sm rounded-full p-2">
          {isPaused ? (
            <Pause className="w-4 h-4 text-foreground" />
          ) : (
            <Play className="w-4 h-4 text-foreground" />
          )}
        </div>
      </div>

      {/* Lightbox Dialog */}
      <Dialog open={selectedIndex !== null} onOpenChange={() => setSelectedIndex(null)}>
        <DialogContent className="max-w-5xl w-full p-0 bg-foreground/95 border-none">
          <div className="relative w-full h-[80vh] flex items-center justify-center">
            <button
              onClick={() => setSelectedIndex(null)}
              className="absolute top-4 right-4 text-background/80 hover:text-background z-10 p-2"
              aria-label="Lukk"
            >
              <X className="w-6 h-6" />
            </button>

            <button
              onClick={handlePrevious}
              className="absolute left-4 text-background/80 hover:text-background z-10 p-2"
              aria-label="Forrige bilde"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>

            {selectedIndex !== null && (
              <img
                src={images[selectedIndex]}
                alt={`${title} bilde ${selectedIndex + 1}`}
                className="max-h-full max-w-full object-contain"
              />
            )}

            <button
              onClick={handleNext}
              className="absolute right-4 text-background/80 hover:text-background z-10 p-2"
              aria-label="Neste bilde"
            >
              <ChevronRight className="w-8 h-8" />
            </button>

            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-background/80 font-sans text-sm">
              {selectedIndex !== null && `${selectedIndex + 1} / ${images.length}`}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default BunadCarousel;
